import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createTextNode, KEY_ENTER_COMMAND, COMMAND_PRIORITY_HIGH } from 'lexical';
import socketService from '../../../services/socketService';

// ~8 rows visible before the menu scrolls. There is deliberately NO result
// cap (the whole list maxes out at 20 snippets): every match renders and the
// menu scrolls. Row height ≈ 29px (0.85rem line + 4px×2 padding); 8 × 29.
const MENU_MAX_HEIGHT = 232;

// One snippet in the typeahead. MenuOption gives us a stable `key` and a DOM
// `ref` — the plugin scrolls `ref.current` into view on arrow-key navigation
// (SCROLL_TYPEAHEAD_OPTION_INTO_VIEW), which is what makes the uncapped,
// scrollable list keyboard-reachable; we just have to attach the ref per row.
class QuickReplyOption extends MenuOption {
  constructor(reply) {
    super(reply.id);
    this.reply = reply;
  }
}

/**
 * While the menu is showing, claim plain Enter at HIGH priority so it selects
 * the highlighted snippet (defaulting to the first row when nothing is
 * highlighted yet) instead of falling through to EnterSendPlugin — which sits
 * at LOW priority, the SAME level as LexicalMenu's own Enter handler, and wins
 * by registration order when the menu opens straight into a query with no
 * highlight (type "/gg" then Enter would send the literal "/gg").
 * Shift+Enter still falls through (newline). Mounted inside the menu portal,
 * so it registers exactly while the menu is open.
 */
function MenuEnterCommand({ onEnter }) {
  const [editor] = useLexicalComposerContext();
  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (event && event.shiftKey) return false;
          return onEnter(event);
        },
        COMMAND_PRIORITY_HIGH
      ),
    [editor, onEnter]
  );
  return null;
}

/**
 * `/` slash typeahead for Quick Replies (modern chat71 "predefined messages").
 *
 * Built directly on Lexical's LexicalTypeaheadMenuPlugin (unlike mentions,
 * which go through lexical-beautiful-mentions). Typing `/` at a word start
 * opens the snippet menu, filtered as you type (prefix matches first, then
 * substring). Selecting a snippet replaces the `/query` text with the
 * snippet's PLAIN text (no pill node — serialize.js needs zero changes) and
 * fires the fire-and-forget use counter.
 *
 * - Empty snippet list → the menu simply never opens (no dead-end UI).
 * - URLs never trigger it: useBasicTypeaheadTriggerMatch only fires at a word
 *   start, and the `/` in `https://…` follows non-whitespace.
 * - Styling reuses ChatPanel's mention popover classes (flip-up above the
 *   composer), passed in as `classes`/`cx` like MentionPlugin.
 */
export default function QuickReplyPlugin({ quickReplies = [], classes, cx }) {
  const [query, setQuery] = useState(null);

  // minLength 0: a bare `/` shows the whole list.
  const checkForMatch = useBasicTypeaheadTriggerMatch('/', { minLength: 0 });
  // Gate on having snippets at all, so an empty list never opens the menu.
  const triggerFn = useCallback(
    (text, editor) => (quickReplies.length ? checkForMatch(text, editor) : null),
    [quickReplies, checkForMatch]
  );

  // Filter: prefix matches first, then substring ("fuzzy-lite"), both
  // case-insensitive. NO cap — all matches render in the scrollable menu.
  const options = useMemo(() => {
    const q = (query || '').toLowerCase();
    const starts = [];
    const contains = [];
    for (const r of quickReplies) {
      const t = (r.text || '').toLowerCase();
      if (t.startsWith(q)) starts.push(r);
      else if (q && t.includes(q)) contains.push(r);
    }
    return [...starts, ...contains].map((r) => new QuickReplyOption(r));
  }, [quickReplies, query]);

  // Called by the plugin INSIDE editor.update(): replace the `/query` text
  // node (split out for us via shouldSplitNodeWithQuery) with the snippet's
  // plain text and land the caret after it.
  const onSelectOption = useCallback((selectedOption, nodeToReplace, closeMenu) => {
    const textNode = $createTextNode(selectedOption.reply.text);
    if (nodeToReplace) nodeToReplace.replace(textNode);
    textNode.select();
    closeMenu();
    // Analytics only — never blocks the insert.
    socketService.markQuickReplyUsed(selectedOption.reply.id);
  }, []);

  const menuRenderFn = useCallback(
    (anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
      if (!anchorElementRef.current || !options.length) return null;
      return createPortal(
        <>
        <MenuEnterCommand
          onEnter={(event) => {
            const option = options[selectedIndex == null ? 0 : selectedIndex];
            if (!option) return false;
            if (event) event.preventDefault();
            selectOptionAndCleanUp(option);
            return true;
          }}
        />
        <ul
          className={classes.mentionPopover}
          // Flip UP over the composer — same trick as MentionPlugin: the
          // library anchors the container just below the caret line, so
          // bottom:100% + marginBottom lifts the menu above it.
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: 26,
            listStyle: 'none',
            // Uncapped result list: ~8 rows visible, the rest scroll (the
            // arrow-key highlight scrolls into view via the option refs).
            maxHeight: MENU_MAX_HEIGHT,
            overflowY: 'auto',
          }}
        >
          {options.map((option, i) => (
            <li
              key={option.key}
              ref={option.setRefElement}
              className={cx(
                classes.mentionRow,
                i === selectedIndex && classes.mentionRowActive
              )}
              role="option"
              aria-selected={i === selectedIndex}
              title={option.reply.text}
              onMouseEnter={() => setHighlightedIndex(i)}
              // Don't steal focus from the editor on click.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectOptionAndCleanUp(option)}
            >
              <span
                style={{
                  maxWidth: 420,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.reply.text}
              </span>
            </li>
          ))}
        </ul>
        </>,
        anchorElementRef.current
      );
    },
    [options, classes, cx]
  );

  return (
    <LexicalTypeaheadMenuPlugin
      options={options}
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      menuRenderFn={menuRenderFn}
    />
  );
}
